import type { Race, RaceFilter } from '../types';
import { normalizeStatus } from '@runner/shared';
import { getRegion } from '../utils/regionMapper';
import { getDday } from '../utils/dday';

type SortField = 'date' | 'name';
type SortOrder = 'asc' | 'desc';

const STATUS_ORDER: Record<Race['status'], number> = {
  open: 0,
  upcoming: 1,
  closed: 2,
  completed: 3,
};

// region grouping uses `race.region` (populated for API data and derived from
// `location.city` for legacy korea.json). For UI badge text we still prefer
// the original city string when it exists — falls back to region otherwise.
function regionKey(race: Race): string | null {
  if (race.region) return race.region;
  if (race.city) return getRegion(race.city);
  return null;
}

function filterRaces(races: Race[], filter: RaceFilter): Race[] {
  return races.filter((race) => {
    if (filter.country && race.country !== filter.country) {
      return false;
    }

    if (filter.region && filter.region.length > 0) {
      const raceRegion = regionKey(race);
      if (!raceRegion || !filter.region.includes(raceRegion)) {
        return false;
      }
    }

    if (filter.distances && filter.distances.length > 0) {
      const hasMatchingDistance = filter.distances.some((d) => race.distances.includes(d));
      if (!hasMatchingDistance) {
        return false;
      }
    }

    if (filter.status && filter.status.length > 0) {
      if (!filter.status.includes(race.status)) {
        return false;
      }
    }

    const raceDate = new Date(race.date);
    if (filter.startDate) {
      const startDate = new Date(filter.startDate);
      if (raceDate < startDate) {
        return false;
      }
    }
    if (filter.endDate) {
      const endDate = new Date(filter.endDate);
      if (raceDate > endDate) {
        return false;
      }
    }

    return true;
  });
}

function sortRaces(races: Race[], field: SortField, order: SortOrder): Race[] {
  const sorted = [...races].sort((a, b) => {
    let comparison = 0;

    if (field === 'date') {
      comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
    } else if (field === 'name') {
      comparison = a.name.localeCompare(b.name);
    }

    return order === 'asc' ? comparison : -comparison;
  });

  return sorted;
}

// 러너의 의사결정 순서로 정렬한다: 지금 신청할 수 있는 것 → 곧 접수 시작 → 접수는 끝났지만
// 레이스는 남은 것 → 지난 대회. 같은 버킷 안에서는 러너가 다음에 볼 날짜(접수 마감/시작,
// 레이스일)를 가장 가까운 것부터 노출한다.
function sortByActionability(races: Race[], now: Date = new Date()): Race[] {
  const sortKey = new Map<string, number>();
  for (const r of races) {
    sortKey.set(
      r.id,
      r.status === 'open' || r.status === 'upcoming'
        ? getDday(r, now).days
        : new Date(r.date).getTime(),
    );
  }

  return [...races].sort((a, b) => {
    const statusDiff = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
    if (statusDiff !== 0) return statusDiff;

    const ak = sortKey.get(a.id) as number;
    const bk = sortKey.get(b.id) as number;

    if (a.status === 'open' || a.status === 'upcoming') {
      return compareDday(ak, bk);
    }

    if (isNaN(ak) && isNaN(bk)) return 0;
    if (isNaN(ak)) return 1;
    if (isNaN(bk)) return -1;

    // closed: 남은 레이스는 빠른 날짜부터 / completed: 최근 종료부터
    return a.status === 'completed' ? bk - ak : ak - bk;
  });
}

// 양수(미래)가 먼저 오되 작은 쪽부터, 음수(지난 것)는 뒤로 밀고 0에 가까운 순.
// NaN 은 마지막으로.
function compareDday(a: number, b: number): number {
  if (isNaN(a) && isNaN(b)) return 0;
  if (isNaN(a)) return 1;
  if (isNaN(b)) return -1;
  const aPast = a < 0;
  const bPast = b < 0;
  if (aPast !== bPast) return aPast ? 1 : -1;
  return aPast ? b - a : a - b;
}

function getRaceById(races: Race[], id: string): Race | undefined {
  return races.find((race) => race.id === id);
}

function groupByCountry(races: Race[]): Record<string, Race[]> {
  return races.reduce(
    (groups, race) => {
      const country = race.country;
      if (!groups[country]) {
        groups[country] = [];
      }
      groups[country].push(race);
      return groups;
    },
    {} as Record<string, Race[]>
  );
}

function groupByRegion(races: Race[]): Record<string, Race[]> {
  return races.reduce(
    (groups, race) => {
      const region = regionKey(race);
      if (region) {
        if (!groups[region]) {
          groups[region] = [];
        }
        groups[region].push(race);
      }
      return groups;
    },
    {} as Record<string, Race[]>
  );
}

function getRegionCounts(races: Race[]): Record<string, number> {
  return races.reduce(
    (counts, race) => {
      const region = regionKey(race);
      if (region) {
        counts[region] = (counts[region] ?? 0) + 1;
      }
      return counts;
    },
    {} as Record<string, number>
  );
}

export const raceService = {
  filterRaces,
  sortRaces,
  sortByActionability,
  normalizeStatus,
  getRaceById,
  groupByCountry,
  groupByRegion,
  getRegionCounts,
};
